import type {
  APIResponse,
  AnalyticsRange,
  CompanyAnalyticsOverview,
  CompanyJobPerformance,
  TalentDiscoveryAnalytics,
  UserAnalyticsOverview,
} from '@tabbio/types';
import { api } from './api';

export class AnalyticsApiError extends Error {
  constructor(message: string, readonly code: string) {
    super(message);
    this.name = 'AnalyticsApiError';
  }
}

function unwrapAnalyticsResponse<T>(response: APIResponse<T>, fallbackMessage: string): T {
  if (response.error) {
    throw new AnalyticsApiError(response.error.message, response.error.code);
  }

  if (!response.data) {
    throw new Error(fallbackMessage);
  }

  return response.data;
}

function buildRangeQuery(range: AnalyticsRange) {
  return `?range=${encodeURIComponent(range)}`;
}

export async function fetchUserAnalyticsOverview(
  range: AnalyticsRange,
): Promise<UserAnalyticsOverview> {
  const response = await api.get<UserAnalyticsOverview>(
    `/api/analytics/user/overview${buildRangeQuery(range)}`,
  );

  return unwrapAnalyticsResponse(response, 'Unable to load your analytics');
}

export async function fetchCompanyAnalyticsOverview(
  range: AnalyticsRange,
): Promise<CompanyAnalyticsOverview> {
  const response = await api.get<CompanyAnalyticsOverview>(
    `/api/analytics/company/overview${buildRangeQuery(range)}`,
  );

  return unwrapAnalyticsResponse(response, 'Unable to load company analytics');
}

export async function fetchCompanyTalentAnalytics(
  range: AnalyticsRange,
): Promise<TalentDiscoveryAnalytics> {
  const response = await api.get<TalentDiscoveryAnalytics>(
    `/api/analytics/company/talent${buildRangeQuery(range)}`,
  );

  return unwrapAnalyticsResponse(response, 'Unable to load talent analytics');
}

export async function fetchCompanyJobAnalytics(
  jobId: string,
  range: AnalyticsRange,
): Promise<CompanyJobPerformance> {
  const response = await api.get<CompanyJobPerformance>(
    `/api/analytics/company/jobs/${encodeURIComponent(jobId)}${buildRangeQuery(range)}`,
  );

  return unwrapAnalyticsResponse(response, 'Unable to load job analytics');
}
